import {
  Component,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  inject,
  signal,
  Renderer2,
} from '@angular/core';
import { AvatarModule } from 'primeng/avatar';
import { AvatarGroupModule } from 'primeng/avatargroup';
import { DrawerModule } from 'primeng/drawer';
import { Popover } from 'primeng/popover';
import {
  MenuService,
  SidebarMenuItem,
} from '../../../core/services/menu.service';
import { GlobalButtonComponent, MenuItemComponent } from 'declarabid-library';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../../../features/auth/services/auth.service';
import { UserRole } from '../../../core/models/auth.model';
import { UsersService } from '../../../features/users/services/users.service';
import { OverlayNotificationsComponent } from '../overlay-notifications/overlay-notifications.component';
import { BrandAssetsService } from '../../../features/config-system/services/brand-assets.service';
import { SkeletonModule } from 'primeng/skeleton';
import { OverlayNotificationsService } from '../../services/overlay-notifications.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-navigation-bar-movil',
  imports: [
    GlobalButtonComponent,
    AvatarModule,
    AvatarGroupModule,
    DrawerModule,
    MenuItemComponent,
    MenuModule,
    ButtonModule,
    OverlayNotificationsComponent,
    SkeletonModule,
    RouterLink,
    Popover,
  ],
  templateUrl: './navigation-bar-movil.component.html',
  styleUrl: './navigation-bar-movil.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class NavigationBarMovilComponent implements OnInit {
  @ViewChild('op') popover!: Popover;

  private readonly _usersService = inject(UsersService).currentUser();
  protected readonly notificationService = inject(OverlayNotificationsService);
  private readonly menuService = inject(MenuService);
  private readonly router = inject(Router);
  private readonly _authService = inject(AuthService);
  private readonly brandAssets = inject(BrandAssetsService);
  private readonly renderer = inject(Renderer2);
  private readonly preventMoreMenuReopen = false;
  private readonly routerEventsSub?: Subscription;

  private removeDocumentClickListener: (() => void) | null = null;

  public sidebarVisible = false;
  public items!: SidebarMenuItem[];
  public itemsOptions: MenuItem[] | undefined;
  public options: MenuItem[] | undefined;
  public currentUserRole = signal<UserRole | undefined>(undefined);
  readonly UserRole = UserRole;
  public showNotification = signal<boolean>(false);
  public logoUrl = this.brandAssets.logoUrl;
  public loadingLogo = signal<boolean>(true);
  public hasUnread = this.notificationService.hasUnread;

  ngOnInit(): void {
    this.loadingLogo.set(true);

    const startedAt = Date.now();
    const MIN_SKELETON_MS = 300;

    const stopLoading = () => {
      const elapsed = Date.now() - startedAt;
      const remaining = MIN_SKELETON_MS - elapsed;

      if (remaining > 0) {
        setTimeout(() => this.loadingLogo.set(false), remaining);
      } else {
        this.loadingLogo.set(false);
      }
    };

    this.brandAssets.load().subscribe({
      next: stopLoading,
      error: stopLoading,
    });

    this.currentUserRole.set(this._usersService.role_value as UserRole);
    this.menuService.setMenuItems(this._usersService.role_value);
    this.menuService.getMenuItems().subscribe((menuItems) => {
      this.items = menuItems;
    });
    this.itemsOptions = [
      {
        label: $localize`Mas opciones`,
        items: [
          // {
          //   label: $localize`Sitio web publico`,
          //   icon: 'pi pi-objects-column',
          //   command: () => {
          //     this.router.navigate(['/admin/public-website']);
          //   },
          // },
          {
            label: $localize`Configuración del sistema`,
            icon: 'pi pi-cog',
            command: () => {
              this.router.navigate(['/admin/config-system']);
            },
          },
        ],
      },
    ];

    this.options = [
      {
        label: $localize`Cerrar sesión`,
        icon: 'pi pi-sign-out',
        command: () => this._authService.logout(),
      },
    ];
  }

  protected onShow() {
    document.body.classList.add('no-scroll');
    this.notificationService.forceRefresh(this._usersService.id);
  }

  protected onHide() {
    document.body.classList.remove('no-scroll');
  }

  protected openNotificationSidebar(): void {
    this.showNotification.set(true);
    this.notificationService.forceRefresh(this._usersService.id);
  }

  protected show(event: Event) {
    this.popover.toggle(event);
  }

  protected openSidebar(event?: Event): void {
    event?.stopPropagation();
    this.sidebarVisible = true;
    this.bindOutsideClickListener();
  }

  protected closeSidebar(): void {
    this.sidebarVisible = false;
    this.unbindOutsideClickListener();
  }

  protected onDrawerVisibleChange(visible: boolean): void {
    this.sidebarVisible = visible;

    if (visible) {
      this.bindOutsideClickListener();
      return;
    }

    this.unbindOutsideClickListener();
  }

  private bindOutsideClickListener(): void {
    if (this.removeDocumentClickListener) return;

    this.removeDocumentClickListener = this.renderer.listen(
      'document',
      'click',
      (event: Event) => {
        if (!this.sidebarVisible) return;

        const target = event.target as HTMLElement | null;
        if (!target) return;

        const drawer = document.querySelector('.nav-mobile-sidebar');
        const menuButton = target.closest('.nav-mobile-open-trigger');

        if (menuButton) return;

        if (drawer && !drawer.contains(target)) {
          this.closeSidebar();
        }
      },
    );
  }

  private unbindOutsideClickListener(): void {
    if (this.removeDocumentClickListener) {
      this.removeDocumentClickListener();
      this.removeDocumentClickListener = null;
    }
  }
}
